import type { CellType, ColumnSource } from "@omegagrid/grid";
import type { SqlColumn } from "../client/sqlStream";

/**
 * The grid columns of a dataset of the SQL editors (rp-editor-sql-result) from the column descriptors of the
 * result stream: the cell type, width and number format a SQL type gets, and the header tooltip with the declared
 * type (setting `editor.sql.result_types`). A leaf module without lit or the grid runtime, so it can be unit tested.
 */

export type SqlColumnFormat = { t: CellType, w?: number | 'auto', z?: string };

/** cell type, width and number format by the bare SQL type name; anything else is text with an automatic width */
const SQL_TYPE_FORMATS = new Map<string, SqlColumnFormat>([
	['bigint', {t: 'n', z: '0'}],
	['binary', {t: 't'}],
	['bit', {t: 'n', z: '0'}],
	['char', {t: 't'}],
	['date', {t: 't', w: 76}],
	['datetime', {t: 't', w: 155}],
	['datetime2', {t: 't', w: 155}],
	['datetimeoffset', {t: 't'}],
	['decimal', {t: 'n'}],
	['float', {t: 'n', z: '0.000'}],
	['int', {t: 'n', z: '0'}],
	['money', {t: 'n', z: '0.000'}],
	['nchar', {t: 't'}],
	['ntext', {t: 't'}],
	['numeric', {t: 'n'}],
	['nvarchar', {t: 't'}],
	['real', {t: 'n', z: '0.000'}],
	['smalldatetime', {t: 't', w: 155}],
	['smallint', {t: 'n', z: '0'}],
	['smallmoney', {t: 'n', z: '0.000'}],
	['text', {t: 't'}],
	['time', {t: 't'}],
	['timestamp', {t: 't'}],
	['tinyint', {t: 'n', z: '0'}],
	['udt', {t: 't'}],
	['uniqueidentifier', {t: 't'}],
	['varbinary', {t: 't'}],
	['varchar', {t: 't'}],
	['xml', {t: 't'}],
]);

const DEFAULT_FORMAT: SqlColumnFormat = {t: 't', w: 'auto'};

/**
 * The cell type, width and number format of a column. A decimal or numeric column shows as many decimals as its
 * scale (when the server sends it); the other types keep the format of their type, or the grid's default.
 */
export function getSqlColumnFormat(column: SqlColumn): SqlColumnFormat {
	const type = (column.type ?? '').toLowerCase();
	const format = SQL_TYPE_FORMATS.get(type) ?? DEFAULT_FORMAT;
	if ((type === 'decimal' || type === 'numeric') && column.scale != null) {
		return {...format, z: column.scale > 0 ? '0.' + '0'.repeat(column.scale) : '0'};
	}
	return format;
}

const HTML_ESCAPES: Record<string, string> = {'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'};
const escapeHtml = (text: string) => text.replace(/[&<>"']/g, c => HTML_ESCAPES[c]);

/**
 * The header tooltip of a column: the declared type in bold (the bare type when the server sends no declared
 * one) and NULL / NOT NULL when the nullability is known; null when nothing about the type is known. HTML, as
 * the grid tooltip renders a string as HTML.
 */
export function createSqlColumnTooltip(column: SqlColumn): string | null {
	const typeName = column.typeName ?? column.type;
	if (!typeName) return null;
	const nullability = column.nullable == null ? '' : (column.nullable ? ' NULL' : ' NOT NULL');
	return `<b>${escapeHtml(typeName)}</b>${nullability}`;
}

export type SqlResultColumnsOptions = {
	/** the declared type as the tooltip of the column header */
	types: boolean,
};

/** the grid columns of a dataset: the data index, the cell type, width and format of the SQL type and the header */
export function createSqlResultColumns(columns: SqlColumn[], options: SqlResultColumnsOptions): ColumnSource[] {
	return columns.map((column, index) => {
		const format = getSqlColumnFormat(column);
		const tooltip = options.types ? createSqlColumnTooltip(column) : null;
		return {
			data: index,
			t: format.t,
			header: tooltip ? [{label: column.name, tooltip}] : column.name,
			w: format.w ?? 'auto',
			z: format.z,
		};
	});
}
